// src/ds/tokens.ts — the token layer (single source; mirrored into the AntD
// ConfigProvider theme in ./AntdTheme and Tailwind in tailwind.config.js).
// Components reference these tokens, never raw hex. Mirrors v2/prototype/admin
// docs/design-system/tokens.json.
import type { TxStatus, RiskBand } from "./types";

export const color = {
  purple: "#57249C",
  purpleDeep: "#33125F",
  purpleLight: "#9747FF",
  purpleMist: "#F1EDF7",
  pink: "#EB3366",
  pinkBright: "#FF0449",
  pinkMist: "#FDEFF3",
  cyan: "#00FFFF",
  ink: "#010317",
  appBg: "#FAFAFA",
  surface: "#FFFFFF",
  fg1: "#1D1D1D",
  fg2: "#423E51",
  fg3: "#6D6584",
  // Secondary/label text. Darkened from the prototype seed (#A5A5A5 ≈ 2.5:1 on white —
  // failed WCAG AA, the "light-on-light" legibility issue) to ~4.7:1 while staying the
  // lightest of the text greys (fg2 > fg3 > muted).
  muted: "#767288",
  border: "rgba(0,0,0,0.08)",
  borderStrong: "rgba(0,0,0,0.16)",
  success: "#2AB673",
  warning: "#CA8E1B",
  danger: "#FF0449",
  info: "#2459BF",
} as const;

export const font = {
  sans: '"Plus Jakarta Sans", system-ui, sans-serif',
  mono: '"JetBrains Mono", ui-monospace, monospace',
} as const;

export const radius = { control: 10, card: 14, pill: 999 } as const;
export const ease = {
  out: "cubic-bezier(0.22,1,0.36,1)",
  standard: "cubic-bezier(0.4,0,0.2,1)",
} as const;
export const duration = { fast: 120, base: 200, narrative: 420 } as const;

export interface StatusToken {
  label: string;
  bg: string;
  fg: string;
  dot: string;
}

/** One status vocabulary, platform-wide. Never hand-roll a coloured tag. */
export const STATUS: Record<TxStatus, StatusToken> = {
  pending: { label: "Pending", bg: "#FFF7E6", fg: "#9A6B00", dot: "#CA8E1B" },
  processing: {
    label: "Processing",
    bg: "#EEF2FF",
    fg: "#2459BF",
    dot: "#2459BF",
  },
  complete: { label: "Complete", bg: "#E7F7EF", fg: "#1B8A57", dot: "#2AB673" },
  failed: { label: "Failed", bg: "#FDEFF3", fg: "#C81E4E", dot: "#FF0449" },
  on_hold: { label: "On hold", bg: "#F1EDF7", fg: "#57249C", dot: "#57249C" },
  review: {
    label: "Needs review",
    bg: "#E9FBFC",
    fg: "#0E7C8B",
    dot: "#15DDED",
  },
};

export function riskToken(score: number): {
  label: string;
  bg: string;
  fg: string;
  bar: string;
} {
  if (score < 20)
    return {
      label: `Low risk · ${score}`,
      bg: "#E7F7EF",
      fg: "#1B8A57",
      bar: "#2AB673",
    };
  if (score < 40)
    return {
      label: `Moderate · ${score}`,
      bg: "#FFF7E6",
      fg: "#9A6B00",
      bar: "#CA8E1B",
    };
  return {
    label: `Elevated · ${score}`,
    bg: "#FDEFF3",
    fg: "#C81E4E",
    bar: "#FF0449",
  };
}

export const riskBand: Record<RiskBand, string> = {
  none: "No flags",
  low: "Unmatched",
  med: "Elevated risk",
  high: "High risk",
};

/**
 * Stacking order for console overlays.
 *
 * AntD portals every popup (Select, DatePicker, Dropdown, Tooltip) to
 * `document.body`, so a dropdown opened inside one of our hand-rolled drawers
 * is painted against these numbers rather than against the drawer's position in
 * the DOM. `popup` MUST stay above `drawer`: with AntD's default
 * `zIndexPopupBase` of 1000, every Select inside a 1100 drawer opened behind
 * the panel and read as "the dropdown doesn't open at all".
 *
 * AntD's own Modal/Drawer raise nested popups automatically; ours cannot, since
 * AntD has no way to know a plain `<div>` is a layer.
 */
export const zLayer = {
  drawer: 1100,
  popup: 1200,
} as const;
